import React, { useState, useEffect } from 'react';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-5 tw-h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function AddToCartConfirmationModal({ isOpen, onClose, onCheckout, onViewCart, cartItemCount, cartTotalPrice, onAddToCart, selectedType }) {
  const [addonData, setAddonData] = useState(null);
  const [addedAddons, setAddedAddons] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setAddedAddons({});
      // Fetch addon data
      fetch('/addons.json')
        .then(response => response.json())
        .then(data => {
          setAddonData(data);
          setIsLoading(false);
        })
        .catch(error => {
          console.error("Error fetching addon data:", error);
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleContentClick = (e) => e.stopPropagation();

  // Helper to format price (Shopify typically provides price in cents)
  const formatPrice = (priceInCents) => {
    return (priceInCents / 100).toFixed(2);
  };

  const handleAddonToCart = (addonVariantId) => {
    if (onAddToCart) {
      onAddToCart(addonVariantId);
      setAddedAddons(prev => ({ ...prev, [addonVariantId]: true }));
    }
  };

  // Get relevant addons based on tank type
  const getRelevantAddons = () => {
    if (!addonData) return [];
    
    const commonAddons = addonData.all || [];
    const typeSpecificAddons = (selectedType && addonData[selectedType]) ? addonData[selectedType] : [];
    return [...commonAddons, ...typeSpecificAddons];
  };

  const relevantAddons = getRelevantAddons();

  return (
    <div
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-60 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-p-6 tw-text-center tw-relative"
        onClick={handleContentClick}
      >
        <button
          onClick={onClose}
          className="tw-absolute tw-top-3 tw-right-3 tw-text-gray-400 hover:tw-text-gray-600 focus:tw-outline-none"
          aria-label="Close modal"
        >
          <CloseIcon />
        </button>

        <div className="tw-mb-4">
          <svg className="tw-w-16 tw-h-16 tw-mx-auto tw-text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>

        <h3 className="tw-text-xl tw-font-semibold tw-text-gray-800 tw-mb-3">
          Item Added to Cart!
        </h3>

        {cartItemCount > 0 && (
          <div className="tw-mb-4 tw-p-3 tw-bg-gray-50 tw-rounded-md">
            <p className="tw-text-sm tw-text-gray-700">
              You now have <span className="tw-font-semibold">{cartItemCount} item{cartItemCount !== 1 ? 's' : ''}</span> in your cart.
            </p>
            <p className="tw-text-sm tw-text-gray-700">
              Cart Total: <span className="tw-font-semibold">${formatPrice(cartTotalPrice)}</span>
            </p>
          </div>
        )}

        {/* Addon Suggestions Section */}
        {relevantAddons.length > 0 && (
          <div className="tw-mb-6">
            <h4 className="tw-text-lg tw-font-medium tw-text-gray-800 tw-mb-3">Would you like to add...</h4>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
              {isLoading ? (
                <div className="tw-col-span-full tw-text-center tw-py-4">Loading suggestions...</div>
              ) : (
                relevantAddons.map((addon, index) => (
                  <div key={index} className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3 tw-flex tw-flex-col tw-items-center">
                    {addon.image && (
                      <img 
                        src={addon.image.startsWith('//') ? `https:${addon.image}` : addon.image} 
                        alt={addon.name} 
                        className="tw-w-16 tw-h-16 tw-object-contain tw-mb-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <h5 className="tw-font-medium tw-text-gray-700 tw-text-sm tw-mb-1">{addon.name}</h5>
                    <p className="tw-text-sm tw-text-gray-600 tw-mb-2">${parseFloat(addon.price).toFixed(2)}</p>
                    <button
                      onClick={() => handleAddonToCart(addon.variantId)}
                      disabled={addedAddons[addon.variantId]}
                      className={`tw-w-full tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-transition-colors tw-duration-150 ${
                        addedAddons[addon.variantId] 
                          ? 'tw-bg-green-500 tw-text-white' 
                          : 'tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white'
                      }`}
                    >
                      {addedAddons[addon.variantId] ? 'Added!' : 'Add to Cart'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <p className="tw-text-gray-600 tw-mb-4">
          What would you like to do next?
        </p>

        <button
          onClick={onCheckout}
          className="tw-w-full tw-mb-3 tw-px-6 tw-py-3 tw-text-lg tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white tw-font-semibold tw-rounded-md tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-red focus:tw-ring-opacity-50 transition-colors duration-200"
        >
          CHECKOUT
        </button>

        <button
          onClick={onViewCart}
          className="tw-w-full tw-px-6 tw-py-2 tw-text-gray-700 tw-font-medium tw-rounded-md hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-300 transition-colors duration-200"
        >
          View Cart
        </button>
      </div>
    </div>
  );
}

export default AddToCartConfirmationModal; 